import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { Outlet, Route, Routes, Link } from "react-router-dom";
import { Briefcase, Wallet, User as UserIcon, Upload as UploadIcon, MessageCircle, CheckSquare, FolderKanban, ListTodo, AlertTriangle, X } from "lucide-react";
import { api, inr, STATUS_LABELS, STATUS_COLORS } from "../lib/api";
import { toast } from "sonner";
import DesignerInbox from "./DesignerInbox";
import ProjectWorkspace from "./admin/ProjectWorkspace";

function DesignerOrders() {
  const [orders, setOrders] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [stats, setStats] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);
  const load = () => api.get("/designer/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); api.get("/designer/dashboard").then(r => setStats(r.data)).catch(() => {}); }, []);

  const accept = async (id) => {
    try { await api.post(`/designer/orders/${id}/accept`); toast.success("Order accepted"); load(); } catch { toast.error("Failed"); }
  };
  const acceptFromPreview = async () => {
    await accept(previewOrder.id);
    setPreviewOrder(null);
  };
  const openRejectFromPreview = () => {
    setRejectFor(previewOrder.id);
    setRejectReason("");
    setPreviewOrder(null);
  };
  const rejectSubmit = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 3) {
      toast.error("Please provide a reason (min 3 characters)");
      return;
    }
    try {
      await api.post(`/designer/orders/${rejectFor}/reject`, { reason: rejectReason.trim() });
      toast.success("Order rejected — admin notified");
      setRejectFor(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };
  const upload = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version_notes", "Initial delivery");
      await api.post(`/designer/orders/${id}/upload-delivery`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Delivery uploaded — customer notified");
      load();
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploadingId(null); }
  };

  const statCards = stats && (
    <div className="grid sm:grid-cols-3 gap-3">
      <Stat label="Active Projects" value={stats.active_projects} accent="text-[#1a3c5e]" icon={FolderKanban} />
      <Stat label="Tasks Due This Week" value={stats.tasks_due_week} accent="text-[#F7941D]" icon={ListTodo} />
      <Stat label="Deadlines At Risk" value={stats.deadlines_at_risk} accent={stats.deadlines_at_risk > 0 ? "text-rose-600" : "text-emerald-600"} icon={AlertTriangle} />
    </div>
  );

  if (orders.length === 0) return (
    <div className="space-y-4">
      {statCards}
      <div className="bg-white rounded-2xl p-10 text-center text-gray-500 border border-[#1a3c5e]/10">No assigned orders yet. New orders appear here once admin assigns them.</div>
    </div>
  );

  return (
    <div className="space-y-4">
      {statCards}
      <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Active Orders</h2>
      <div className="space-y-3">
      {orders.map(o => (
        <div key={o.id} className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5" data-testid={`designer-order-${o.id}`}>
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_COLORS[o.status] || "bg-gray-100"}`}>{STATUS_LABELS[o.status]}</span>
              <div className="font-display text-lg font-bold text-[#1a3c5e] mt-1.5">{o.service_name}</div>
              <div className="text-xs text-gray-500">Customer: {o.customer?.display_name || "—"} · {o.customer?.city || "—"}{o.customer?.state ? `, ${o.customer.state}` : ""}</div>
              {o.deadline && <div className="text-xs text-[#F7941D] font-semibold mt-1">Deadline: {new Date(o.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>}
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              {o.status === "assigned" && (
                <button onClick={() => setPreviewOrder(o)} className="px-4 py-2 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-sm font-semibold rounded-xl" data-testid={`view-details-${o.id}`}>View Details</button>
              )}
              {(o.status === "in_progress" || o.status === "delivered") && (
                <label className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white text-sm font-semibold rounded-xl cursor-pointer flex items-center gap-1.5">
                  <UploadIcon className="w-4 h-4" />
                  {uploadingId === o.id ? "Uploading..." : (o.status === "delivered" ? "Upload Revision" : "Upload Delivery")}
                  <input type="file" hidden disabled={uploadingId === o.id} onChange={(e) => upload(o.id, e.target.files[0])} data-testid={`upload-delivery-${o.id}`} />
                </label>
              )}
              <Link to={`/designer/projects/${o.id}`} className="text-xs text-[#F7941D] font-semibold hover:underline">Manage Project</Link>
            </div>
          </div>
        </div>
      ))}
      </div>

      {previewOrder && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewOrder(null)} data-testid="designer-preview-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_COLORS[previewOrder.status] || "bg-gray-100"}`}>{STATUS_LABELS[previewOrder.status]}</span>
                <h3 className="font-display text-xl font-bold text-[#1a3c5e] mt-1.5">{previewOrder.service_name}</h3>
              </div>
              <button onClick={() => setPreviewOrder(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm border-t border-gray-100 pt-4">
              <div>
                <div className="text-xs text-gray-500 uppercase font-semibold">Customer</div>
                <div className="font-semibold text-[#1a3c5e] mt-0.5">{previewOrder.customer?.display_name || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-semibold">Location</div>
                <div className="font-semibold text-[#1a3c5e] mt-0.5">{previewOrder.customer?.city || "—"}{previewOrder.customer?.state ? `, ${previewOrder.customer.state}` : ""}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-semibold">Package Value</div>
                <div className="font-semibold text-[#1a3c5e] mt-0.5">{inr(previewOrder.amount || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-semibold">Deadline</div>
                <div className="font-semibold text-[#1a3c5e] mt-0.5">{previewOrder.deadline ? new Date(previewOrder.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</div>
              </div>
            </div>
            {previewOrder.status === "assigned" && (
              <div className="flex gap-2 mt-6">
                <button onClick={acceptFromPreview} data-testid="preview-accept-btn" className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl">Accept Project</button>
                <button onClick={openRejectFromPreview} data-testid="preview-reject-btn" className="flex-1 py-2.5 border-2 border-red-200 text-red-600 hover:bg-red-50 font-bold rounded-xl">Reject</button>
              </div>
            )}
          </div>
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRejectFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-[#1a3c5e]">Reject Order</h3>
            <p className="text-xs text-gray-500 mt-1 mb-3">Tell admin why you can't take this order. It'll be reassigned.</p>
            <textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Schedule conflict, scope outside expertise..."
              data-testid="reject-reason-input"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRejectFor(null)} className="px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-xl">Cancel</button>
              <button onClick={rejectSubmit} data-testid="reject-submit-btn" className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DesignerProfile() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newSkill, setNewSkill] = useState("");

  const load = () => api.get("/designer/profile").then(r => setProfile(r.data));
  useEffect(() => { load(); }, []);

  if (!profile) return <div className="bg-white rounded-2xl p-10 text-center text-gray-500">Loading profile…</div>;

  const update = (k, v) => setProfile({ ...profile, [k]: v });
  const addSkill = () => {
    const s = newSkill.trim();
    if (!s) return;
    if ((profile.skills || []).includes(s)) { toast.error("Already added"); return; }
    update("skills", [...(profile.skills || []), s]);
    setNewSkill("");
  };
  const removeSkill = (s) => update("skills", (profile.skills || []).filter(x => x !== s));
  const removeSample = (url) => update("samples", (profile.samples || []).filter(x => x !== url));

  const uploadSample = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/designer/upload-sample", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      update("samples", [...(profile.samples || []), fullUrl]);
      toast.success("Sample added — save to publish");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/designer/profile", {
        bio: profile.bio || "",
        skills: profile.skills || [],
        samples: profile.samples || [],
        avatar: profile.avatar || "",
        role: profile.role || "",
        years_experience: profile.years_experience || 0,
      });
      toast.success("Profile saved");
      load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Profile</h2>
            <p className="text-xs text-gray-500 mt-1">Customers see this on portfolio pages. Make it shine.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#1a3c5e]/60">Status</div>
              {profile.is_verified ? (
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 text-xs font-bold uppercase">Verified</span>
              ) : (
                <span className="px-2 py-1 rounded bg-amber-100 text-amber-700 text-xs font-bold uppercase">Pending</span>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#1a3c5e]/60">Rating</div>
              <div className="font-display font-bold text-[#F7941D]">★ {profile.avg_rating || 0}</div>
            </div>
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs text-gray-500">Name</div>
            <div className="font-semibold text-[#1a3c5e]">{profile.name}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">City</div>
            <div className="font-semibold text-[#1a3c5e]">{profile.city || "—"}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <h3 className="font-display font-bold text-[#1a3c5e] mb-3">Professional Details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-[#1a3c5e]">Role / Title</label>
            <input
              value={profile.role || ""}
              onChange={(e) => update("role", e.target.value)}
              placeholder="e.g. Senior Architectural Designer"
              data-testid="designer-role-input"
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1a3c5e]">Years of Experience</label>
            <input
              type="number"
              min={0}
              max={60}
              value={profile.years_experience || 0}
              onChange={(e) => update("years_experience", parseInt(e.target.value, 10) || 0)}
              data-testid="designer-years-input"
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
            />
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-center">
          <div className="p-3 rounded-xl bg-[#FEF3E2]/50">
            <div className="font-display text-2xl font-extrabold text-[#1a3c5e]">{profile.total_completed || 0}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Completed</div>
          </div>
          <div className="p-3 rounded-xl bg-[#FEF3E2]/50">
            <div className="font-display text-2xl font-extrabold text-[#1a3c5e]">{profile.on_time_delivery_pct ?? 100}%</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">On-time</div>
          </div>
          <div className="p-3 rounded-xl bg-[#FEF3E2]/50">
            <div className="font-display text-2xl font-extrabold text-[#1a3c5e]">★ {profile.avg_rating || 0}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Rating</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <h3 className="font-display font-bold text-[#1a3c5e] mb-3">Bio</h3>
        <textarea
          rows={4}
          value={profile.bio || ""}
          onChange={(e) => update("bio", e.target.value)}
          placeholder="Tell customers about your experience, specialties, design philosophy..."
          data-testid="designer-bio-input"
          className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
        />
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <h3 className="font-display font-bold text-[#1a3c5e] mb-3">Skills</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {(profile.skills || []).map(s => (
            <span key={s} data-testid={`skill-${s}`} className="px-2.5 py-1.5 bg-[#FEF3E2] text-[#F7941D] text-xs font-bold rounded-lg flex items-center gap-1.5">
              {s}
              <button type="button" onClick={() => removeSkill(s)} className="hover:text-red-600">×</button>
            </span>
          ))}
          {(!profile.skills || profile.skills.length === 0) && <span className="text-xs text-gray-500">No skills yet — add some below.</span>}
        </div>
        <div className="flex gap-2">
          <input
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
            placeholder="e.g. AutoCAD, SketchUp, V-Ray..."
            data-testid="designer-skill-input"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
          />
          <button type="button" onClick={addSkill} data-testid="designer-skill-add" className="px-4 py-2 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-sm font-semibold rounded-lg">Add</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-[#1a3c5e]">Sample Work</h3>
          <label className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-[#F7941D]/40 rounded-lg text-xs font-semibold text-[#F7941D] cursor-pointer hover:bg-[#FEF3E2]/40 ${uploading ? "opacity-50" : ""}`}>
            <UploadIcon className="w-3.5 h-3.5" />
            {uploading ? "Uploading..." : "+ Add Sample"}
            <input type="file" accept="image/*" hidden disabled={uploading} onChange={(e) => uploadSample(e.target.files[0])} data-testid="designer-sample-upload" />
          </label>
        </div>
        {(profile.samples || []).length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Add at least 3 sample renders to attract more orders.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(profile.samples || []).map((url, i) => (
              <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden border border-gray-200 group">
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removeSample(url)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sticky bottom-4 bg-white rounded-2xl border-2 border-[#F7941D] shadow-xl p-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="designer-save-profile" className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-50 text-white font-bold rounded-xl">
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "", icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5">
      <div className="flex items-center gap-1.5 text-xs text-gray-500 uppercase tracking-wider font-semibold">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`font-display text-3xl font-extrabold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

import DesignerTasks from "./DesignerTasks";
import DesignerWallet from "./DesignerWallet";

export default function DesignerPages() {
  return (
    <Routes>
      <Route element={
        <DashboardLayout
          title="Designer Dashboard"
          testIdPrefix="designer"
          links={[
            { to: "/designer", label: "My Orders", icon: Briefcase, end: true },
            { to: "/designer/tasks", label: "Tasks", icon: CheckSquare },
            { to: "/designer/inbox", label: "Inbox", icon: MessageCircle },
            { to: "/designer/wallet", label: "Wallet", icon: Wallet },
            { to: "/designer/profile", label: "Profile", icon: UserIcon },
          ]}
        />
      }>
        <Route index element={<DesignerOrders />} />
        <Route path="projects/:id" element={<ProjectWorkspace />} />
        <Route path="tasks" element={<DesignerTasks />} />
        <Route path="inbox" element={<DesignerInbox />} />
        <Route path="wallet" element={<DesignerWallet />} />
        <Route path="profile" element={<DesignerProfile />} />
      </Route>
    </Routes>
  );
}
